/**
 * Print Context Builder
 *
 * The editor's print context of a template: the rectangle the one formula
 * `templateRect` gives on that template (product-transformer computes it
 * once), the area's millimetres and the template's Pixel Scale. The canvas
 * JSON is stored divided by exactly that Pixel Scale (R34), so the context
 * never derives it from the rounded rectangle.
 */

import type { CreatePrintContextParams } from '@/types/printContext';
import type { Product as ApiProduct } from '@/api/products';
import {
  getConfiguredPositionTemplates,
  transformProduct,
  type UIProductMockupTemplate,
} from '@/lib/product-transformer';

/**
 * Build print context params from a UI mockup template (matches CanvasEditor).
 */
export function buildPrintContextParamsFromTemplate(
  template: UIProductMockupTemplate
): CreatePrintContextParams {
  const rect = template.printAreaRect;
  return {
    widthPx: rect.width,
    heightPx: rect.height,
    widthMm: template.printAreaWidthMm,
    heightMm: template.printAreaHeightMm,
    pxPerMm: template.pxPerMm,
    offsetX: rect.x,
    offsetY: rect.y,
    mockupWidthPx: template.mockupWidthPx,
    mockupHeightPx: template.mockupHeightPx,
    ...(template.printAreaSizeTier !== undefined && {
      sizeTier: template.printAreaSizeTier,
    }),
    ...(template.printAreaSizeTierOverride !== undefined && {
      sizeTierOverride: template.printAreaSizeTierOverride,
    }),
    ...(template.productId !== undefined && { productId: template.productId }),
    ...(template.printAreaProductClass !== undefined && {
      productClass: template.printAreaProductClass,
    }),
    ...(template.printAreaCarryOverPercent !== undefined && {
      carryOverPercent: template.printAreaCarryOverPercent,
    }),
    ...(template.printAreaCarryOverAnchor !== undefined && {
      carryOverAnchor: template.printAreaCarryOverAnchor,
    }),
    ...(template.printAreaCarryOverTopOffsetMm !== undefined && {
      carryOverTopOffsetMm: template.printAreaCarryOverTopOffsetMm,
    }),
  };
}

/**
 * The configured template a product shows for a position: the default
 * template where there is one (`getConfiguredPositionTemplates`), null when
 * the position is not configured (R31) or the product is unknown.
 */
export function configuredTemplateOf(
  product: ApiProduct | null | undefined,
  positionSlug: string
): UIProductMockupTemplate | null {
  if (!product) return null;
  return (
    getConfiguredPositionTemplates(transformProduct(product).mockupTemplates)
      .filter((t) => t.positionKey === positionSlug)
      .at(0) ?? null
  );
}

/**
 * Print context params of an API product's position, through the same
 * transformer the cards use. Null when the position is not configured:
 * the caller skips the position instead of inventing an area.
 */
export function buildPrintContextParamsForPosition(
  product: ApiProduct | null | undefined,
  positionSlug: string
): CreatePrintContextParams | null {
  const template = configuredTemplateOf(product, positionSlug);
  return template ? buildPrintContextParamsFromTemplate(template) : null;
}
